import React from 'react';
import { createTheme, CssBaseline, ThemeProvider, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import '../styles/vars.css';
import '../styles/animations.css';

export type ColorMode = 'light' | 'dark';

export interface ColorModeContextType {
  mode: ColorMode;
  toggle: () => void;
}

export const ColorModeContext = React.createContext<ColorModeContextType>({
  mode: 'light',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  toggle: () => {},
});

const STORAGE_KEY = 'app:color-mode:v1';

function loadStoredMode(): ColorMode | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark') return v;
    return null;
  } catch {
    return null;
  }
}

function persistMode(mode: ColorMode) {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // ignore storage errors (private mode, etc.)
  }
}

export function useInitialMode(): ColorMode {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const stored = loadStoredMode();
  return stored ?? (prefersDark ? 'dark' : 'light');
}

export function createAppTheme(mode: ColorMode) {
  const light = {
    mode: 'light' as const,
    primary: { main: getCssVar('--medium-blue', '#2563EB'), dark: getCssVar('--dark-blue', '#1D4ED8'), light: getCssVar('--light-blue', '#60A5FA'), contrastText: '#FFFFFF' },
    secondary: { main: getCssVar('--dark-gray', '#475569') },
    background: { default: '#FFFFFF', paper: getCssVar('--lighter-gray', '#F8FAFC') },
    text: { primary: getCssVar('--darker-gray', '#0F172A'), secondary: getCssVar('--dark-gray', '#475569') },
    divider: getCssVar('--light-gray', '#E2E8F0'),
  };

  const dark = {
    mode: 'dark' as const,
    primary: { main: getCssVar('--medium-blue', '#2563EB'), dark: getCssVar('--dark-blue', '#1D4ED8'), light: getCssVar('--light-blue', '#60A5FA'), contrastText: '#FFFFFF' },
    secondary: { main: getCssVar('--medium-gray', '#A3A3A3') },
    background: { default: '#0B0F14', paper: '#0F1620' },
    text: { primary: '#EAEAEA', secondary: getCssVar('--medium-gray', '#A3A3A3') },
    divider: '#1F2937',
  };

  function getCssVar(name: string, fallback: string) {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
  }

  return createTheme({
    palette: mode === 'light' ? light : dark,
    typography: {
      fontSize: 14,
      h1: { fontWeight: 800 },
      h2: { fontWeight: 800 },
      h3: { fontWeight: 800 },
      h4: { fontWeight: 700 },
    },
    components: {
      MuiButton: {
        defaultProps: { variant: 'contained' },
        styleOverrides: {
          root: { textTransform: 'none', borderRadius: 999, fontWeight: 700 },
          containedPrimary: ({ theme }) => ({
            backgroundColor: alpha(theme.palette.primary.main, 0.16),
            color: theme.palette.text.primary,
            boxShadow: 'none',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.22),
              boxShadow: 'none',
            },
            '&:active': {
              backgroundColor: alpha(theme.palette.primary.main, 0.28),
            },
          }),
          outlinedPrimary: ({ theme }) => ({
            borderColor: alpha(theme.palette.primary.main, 0.3),
            color: theme.palette.text.primary,
            '&:hover': {
              borderColor: alpha(theme.palette.primary.main, 0.5),
              backgroundColor: alpha(theme.palette.primary.main, 0.06),
            },
          }),
        },
      },
      MuiLink: {
        styleOverrides: { root: { fontWeight: 600 } },
      },
    },
  });
}

type Props = { children: React.ReactNode };

export function ThemeProviderWithMode({ children }: Props) {
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const [mode, setMode] = React.useState<ColorMode>(() => loadStoredMode() ?? (prefersDark ? 'dark' : 'light'));

  const ctx = React.useMemo<ColorModeContextType>(() => ({
    mode,
    toggle: () => setMode((m: ColorMode) => {
      const next = m === 'light' ? 'dark' : 'light';
      persistMode(next);
      return next;
    }),
  }), [mode]);

  const theme = React.useMemo(() => createAppTheme(mode), [mode]);

  return (
    <ColorModeContext.Provider value={ctx}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}
